/**
 * ARANOX Design System — Design Tokens REV12
 */

export const aranoxColors = {
  brand: {
    50: '#ecfdfb',
    100: '#cffaf5',
    200: '#a3f3ea',
    300: '#6ee7dc',
    400: '#38d2c8',
    500: '#18b5ae',
    600: '#0d918e',
    700: '#0f7373',
    800: '#115c5d',
    900: '#104d4e',
    950: '#062f32',
  },
  navy: {
    50: '#f1f7fa',
    100: '#dcecf2',
    200: '#bdd9e5',
    300: '#90bed2',
    400: '#5d9db9',
    500: '#3c809e',
    600: '#2f6681',
    700: '#29536a',
    800: '#254657',
    900: '#213b4a',
    950: '#071522',
  },
  semantic: {
    success: '#1d9a6c',
    warning: '#d78b19',
    error: '#d34a4a',
    info: '#297eb8',
  },
  neutral: {
    0: '#ffffff',
    50: '#f7fafb',
    100: '#edf3f5',
    200: '#dce6e9',
    300: '#c3d1d6',
    400: '#8ea4ad',
    500: '#657c86',
    600: '#4c616a',
    700: '#344850',
    800: '#20333b',
    900: '#12242c',
    950: '#08171e',
    1000: '#000000',
  },
} as const;

export const aranoxTypography = {
  fontFamily: '"Inter", "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  fontFamilyMono: '"IBM Plex Mono", "Roboto Mono", Consolas, "Courier New", monospace',
  fontWeight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },
  fontSize: {
    caption: '0.75rem',
    small: '0.8125rem',
    body: '0.875rem',
    bodyLarge: '1rem',
    h5: '1.125rem',
    h4: '1.25rem',
    h3: '1.5rem',
    h2: '2rem',
    h1: '2.5rem',
    display: '3rem',
  },
  lineHeight: {
    tight: 1.2,
    heading: 1.3,
    body: 1.5,
    relaxed: 1.65,
  },
} as const;

export const aranoxSpacing = {
  0: 0,
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  8: 32,
  10: 40,
  12: 48,
  16: 64,
  24: 96,
} as const;

export const aranoxRadius = {
  none: 0,
  xs: 2,
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
  '2xl': 24,
  '3xl': 32,
  full: 9999,
} as const;

export const aranoxShadow = {
  xs: '0 1px 2px rgba(4, 18, 31, 0.06)',
  sm: '0 4px 12px rgba(4, 18, 31, 0.08)',
  md: '0 10px 28px rgba(4, 18, 31, 0.12)',
  lg: '0 18px 48px rgba(4, 18, 31, 0.16)',
  xl: '0 28px 72px rgba(4, 18, 31, 0.22)',
  glass: '0 16px 48px rgba(4, 18, 31, 0.16)',
  overlay: '0 32px 96px rgba(4, 18, 31, 0.32)',
} as const;

export const aranoxMotion = {
  duration: {
    instant: '80ms',
    fast: '160ms',
    normal: '240ms',
    slow: '360ms',
    deliberate: '520ms',
  },
  easing: {
    standard: 'cubic-bezier(0.2, 0, 0, 1)',
    emphasized: 'cubic-bezier(0.2, 0, 0, 1.2)',
    entrance: 'cubic-bezier(0, 0, 0, 1)',
    exit: 'cubic-bezier(0.3, 0, 1, 1)',
  },
} as const;

export const aranoxBreakpoints = {
  xs: 0,
  sm: 600,
  md: 960,
  lg: 1280,
  xl: 1600,
} as const;

export const aranoxZIndex = {
  base: 0,
  sticky: 100,
  header: 110,
  sidebar: 120,
  dropdown: 1000,
  modal: 1300,
  toast: 1400,
  tooltip: 1500,
} as const;

export const aranoxDesignTokens = {
  colors: aranoxColors,
  typography: aranoxTypography,
  spacing: aranoxSpacing,
  radius: aranoxRadius,
  shadow: aranoxShadow,
  motion: aranoxMotion,
  breakpoints: aranoxBreakpoints,
  zIndex: aranoxZIndex,
} as const;

export type AranoxDesignTokens = typeof aranoxDesignTokens;
